import { LogIn, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useClockIn, useClockOut } from '@/hooks/employee-portal';
import { ApiError } from '@/lib/api';
import { formatDuration, formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import type { TimeTrackingCard as TimeTrackingData } from '@shared/schemas/employee-portal';

/*
 * The dashboard's hero — ROADMAP.md §6.5, FINDINGS.md §11.5.
 *
 * **Clocking in is the first thing a cleaner does, so it is the first thing on
 * the screen.** The prototype gets this right and it is preserved: a large
 * monospace timer, what state you are in, and a full-width button.
 *
 * **This is not Start Job.** Clocking in starts a shift and one shift covers
 * several jobs; Start Job changes one job and lives on the job screen. The Airo
 * roadmap conflated them (FINDINGS.md §11.2), so the card says which is which
 * rather than leaving somebody to find out.
 *
 * The timer counts from the server's `clockInAt` towards a clock anchored to
 * the server's `serverNow`, not to `Date.now()`. A phone whose clock is ten
 * minutes fast would otherwise show a shift ten minutes longer than payroll
 * will pay for — and the number a cleaner reads and the number on the timesheet
 * have to be the same number.
 */

/** `01:23:45`. Tabular figures, so the digits do not jitter as they tick. */
function elapsed(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const secs = Math.floor(clamped % 60);

  return [hours, minutes, secs].map((part) => String(part).padStart(2, '0')).join(':');
}

export function TimeTrackingCard({ time }: { time: TimeTrackingData }) {
  const toast = useToast();
  const clockIn = useClockIn();
  const clockOut = useClockOut();

  const open = time.openShift;

  /*
   * The offset between this browser's clock and the server's, measured once
   * when the shift was read. Everything below counts against the server's, so
   * a wrong device clock changes nothing.
   */
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    // No reset when there is no shift: the display below renders `00:00:00`
    // directly in that case, so a stale count is never on screen and clearing
    // it would be a setState in an effect for no visible reason.
    if (!open) return;

    const startedAt = Date.parse(open.clockInAt);
    const skew = Date.now() - Date.parse(open.serverNow);

    function tick(): void {
      setSeconds(Math.floor((Date.now() - skew - startedAt) / 1000));
    }

    tick();
    const timer = setInterval(tick, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [open]);

  const busy = clockIn.isPending || clockOut.isPending;

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-h3 text-ink-900">Time tracking</h2>
        <p className="text-meta text-ink-500">
          {/* Zero is `0`, and it renders as `0m` rather than a dash (P1). */}
          {formatDuration(time.today.minutes)} clocked today
        </p>
      </div>

      <p
        // `tabular-nums` so the width does not change as the digits do — a timer
        // that shifts sideways every second is a timer people stop watching.
        className="mt-3 text-center font-mono text-[40px] tabular-nums leading-none text-ink-900"
        // Announced once a minute rather than once a second, which is what a
        // per-second live region would do to a screen reader.
        aria-live="off"
      >
        {open ? elapsed(seconds) : '00:00:00'}
      </p>

      <p className="mt-2 text-center text-body text-ink-500">
        {open ? `On the clock since ${formatInstant(open.clockInAt)}` : 'Not currently clocked in'}
      </p>

      <div className="mt-4">
        {open ? (
          <Button
            size="lg"
            full
            variant="outline"
            disabled={busy}
            onClick={() => {
              clockOut.mutate(undefined, {
                onSuccess: (shift) => {
                  toast.success(
                    `Clocked out — ${formatDuration(shift.durationMinutes ?? 0)} on this shift.`,
                  );
                },
                onError: (error) => {
                  toast.error(error instanceof ApiError ? error.message : 'Could not clock out.');
                },
              });
            }}
          >
            <LogOut aria-hidden="true" />
            {clockOut.isPending ? 'Clocking out…' : 'Clock out'}
          </Button>
        ) : (
          <Button
            size="lg"
            full
            disabled={busy}
            onClick={() => {
              clockIn.mutate(
                // No job id: a shift covers whatever the day holds, and tying it
                // to one job here would be guessing which one.
                { jobId: null },
                {
                  onSuccess: () => {
                    toast.success('Clocked in.');
                  },
                  onError: (error) => {
                    toast.error(error instanceof ApiError ? error.message : 'Could not clock in.');
                  },
                },
              );
            }}
          >
            <LogIn aria-hidden="true" />
            {clockIn.isPending ? 'Clocking in…' : 'Clock in'}
          </Button>
        )}
      </div>

      <p className="mt-2 text-center text-meta text-ink-500">
        {/* The distinction, on the screen rather than in a comment. */}
        This is your shift. Starting a job is a separate button on the job.
      </p>
    </section>
  );
}
